import { useAuth } from "@clerk/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, ChevronRight } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { cn } from "@ndd/ui";
import type {
  AlwaysAiUser,
  AppointmentListItem,
  AppointmentListResponse,
} from "../../../../shared/api-types";
import { authenticatedApi } from "@/lib/api";
import {
  appointmentsListApiRequest,
  appointmentsTodayCount,
  deviceTimezone,
  formatAppointmentTime,
  groupAppointments,
  shortDateLabel,
  upNextAppointment,
  type AppointmentListSearch,
} from "@/lib/appointments";
import { isAppointmentPath, readsOwnAppointmentRows } from "@/lib/permissions";
import {
  activeAppointmentIdFor,
  appointmentsTooltipLabel,
  isTodayPanelVisible,
  todayCountLabel,
} from "@/lib/siderail-state";
import { useViewedRdName } from "@/lib/use-viewed-rd-name";
import { DESKTOP_QUERY } from "./shell-layout";
import {
  AppointmentsLabel,
  SiderailIcon,
  SiderailLabelClip,
  SiderailSectionLabel,
  SiderailTodayRowContent,
  SiderailTooltip,
  siderailTodayRowClass,
} from "./siderail-parts";

// The RD shell's Appointments entry: the home link on /appointments, and
// elsewhere the schedule button with its Today panel. A composer (router
// links, the Today query), so no test imports it; its pure decisions live in
// lib/siderail-state.ts and its presentation in siderail-parts.tsx (NO-754).
// The single rail has its own entry (rail-appointments.tsx), sharing the view
// scope, the Today query, the label and the Today rows from here.

/** The Appointments list the URL selects, and whether it is someone else's. */
export function useAppointmentsViewScope(user: AlwaysAiUser): {
  listSearch: AppointmentListSearch;
  viewingOther: boolean;
} {
  const listSearch = useSearch({ strict: false }) as AppointmentListSearch;

  return { listSearch, viewingOther: !readsOwnAppointmentRows(user, listSearch.rdId) };
}

export function SiderailAppointmentsSection({
  user,
  pathname,
  collapsed,
  isDesktop,
  drawerOpen,
  onExpand,
}: {
  user: AlwaysAiUser;
  pathname: string;
  collapsed: boolean;
  isDesktop: boolean;
  drawerOpen: boolean;
  onExpand: () => void;
}): ReactElement {
  const { listSearch, viewingOther } = useAppointmentsViewScope(user);
  const viewedRdName = useViewedRdName(viewingOther ? listSearch.rdId : undefined);
  const [scheduleOpen, toggleSchedule] = useScheduleDisclosure(
    collapsed,
    isDesktop,
    onExpand
  );
  const panelOpen = isTodayPanelVisible({
    scheduleOpen,
    collapsed,
    isDesktop,
    drawerOpen,
  });

  return (
    <SiderailAppointmentsItem
      pathname={pathname}
      listSearch={listSearch}
      viewingOther={viewingOther}
      viewedRdName={viewedRdName}
      collapsed={collapsed}
      scheduleOpen={scheduleOpen}
      panelOpen={panelOpen}
      onToggleSchedule={toggleSchedule}
    />
  );
}

function SiderailAppointmentsItem({
  pathname,
  listSearch,
  viewingOther,
  viewedRdName,
  collapsed,
  scheduleOpen,
  panelOpen,
  onToggleSchedule,
}: {
  pathname: string;
  listSearch: AppointmentListSearch;
  viewingOther: boolean;
  viewedRdName: string | null;
  collapsed: boolean;
  scheduleOpen: boolean;
  panelOpen: boolean;
  onToggleSchedule: () => void;
}): ReactElement {
  const label = { viewingOther, viewedRdName };
  const tooltip = appointmentsTooltipLabel(viewingOther, viewedRdName);

  if (pathname === "/appointments") {
    // On home the canvas already shows every Appointment, so the siderail
    // entry is pure navigation.
    return (
      <SiderailAppointmentsHomeLink
        {...label}
        tooltip={tooltip}
        collapsed={collapsed}
        listSearch={listSearch}
      />
    );
  }

  return (
    <>
      <SiderailScheduleButton
        {...label}
        tooltip={tooltip}
        collapsed={collapsed}
        scheduleOpen={scheduleOpen}
        onAppointmentsRoute={isAppointmentPath(pathname)}
        onToggle={onToggleSchedule}
      />
      {panelOpen ? (
        <SiderailSchedulePanel
          activeAppointmentId={activeAppointmentIdFor(pathname)}
          listSearch={listSearch}
        />
      ) : null}
    </>
  );
}

function useScheduleDisclosure(
  collapsed: boolean,
  isDesktop: boolean,
  onExpand: () => void
): [boolean, () => void] {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Collapsing the siderail closes the disclosure (a density reset), keeping the
  // button's aria-expanded truthful instead of claiming an invisible panel.
  useEffect(() => {
    if (collapsed && isDesktop) setScheduleOpen(false);
  }, [collapsed, isDesktop]);

  const toggleSchedule = (): void => {
    // The compact siderail cannot render the schedule panel, so
    // activating Today expands the siderail first.
    if (collapsed && window.matchMedia(DESKTOP_QUERY).matches) {
      onExpand();
      setScheduleOpen(true);

      return;
    }

    setScheduleOpen((value) => !value);
  };

  return [scheduleOpen, toggleSchedule];
}

function SiderailAppointmentsHomeLink({
  tooltip,
  collapsed,
  listSearch,
  viewingOther,
  viewedRdName,
}: {
  tooltip: string;
  collapsed: boolean;
  listSearch: AppointmentListSearch;
  viewingOther: boolean;
  viewedRdName: string | null;
}): ReactElement {
  return (
    <SiderailTooltip label={tooltip} collapsed={collapsed}>
      <Link
        to="/appointments"
        search={listSearch}
        aria-current="page"
        aria-label={viewedRdName ? `Appointments — ${viewedRdName}` : undefined}
        className="flex min-h-10 items-center rounded-lg bg-accent font-sans text-body-lg font-bold text-foreground"
      >
        <SiderailIcon icon={CalendarDays} className="text-foreground" />
        <SiderailLabelClip collapsed={collapsed}>
          <AppointmentsLabel viewingOther={viewingOther} viewedRdName={viewedRdName} />
        </SiderailLabelClip>
      </Link>
    </SiderailTooltip>
  );
}

function SiderailScheduleButton({
  tooltip,
  collapsed,
  scheduleOpen,
  onAppointmentsRoute,
  onToggle,
  viewingOther,
  viewedRdName,
}: {
  tooltip: string;
  collapsed: boolean;
  scheduleOpen: boolean;
  onAppointmentsRoute: boolean;
  onToggle: () => void;
  viewingOther: boolean;
  viewedRdName: string | null;
}): ReactElement {
  // Appointment detail keeps the Calendar section visibly active when the
  // siderail is compact.
  const compactActive = collapsed && onAppointmentsRoute;

  return (
    <SiderailTooltip label={tooltip} collapsed={collapsed}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={scheduleOpen}
        className={cn(
          "flex h-10 w-full items-center rounded-lg text-left font-sans text-body-lg font-bold text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          compactActive && "lg:bg-accent"
        )}
      >
        <SiderailIcon
          icon={CalendarDays}
          className={cn("text-muted-foreground", compactActive && "lg:text-foreground")}
        />
        <SiderailLabelClip collapsed={collapsed}>
          <span className="flex items-center">
            <AppointmentsLabel
              viewingOther={viewingOther}
              viewedRdName={viewedRdName}
            />
            <ChevronRight
              aria-hidden="true"
              className={cn(
                "ml-auto mr-2 size-4 text-subtle transition-transform",
                scheduleOpen && "rotate-90"
              )}
            />
          </span>
        </SiderailLabelClip>
      </button>
    </SiderailTooltip>
  );
}

function SiderailSchedulePanel({
  activeAppointmentId,
  listSearch,
}: {
  activeAppointmentId: string | null;
  listSearch: AppointmentListSearch;
}): ReactElement {
  return (
    <div>
      <SiderailTodayPanel
        activeAppointmentId={activeAppointmentId}
        listSearch={listSearch}
      />
      <Link
        to="/appointments"
        search={listSearch}
        className="mt-1.5 flex items-center gap-1.5 rounded-lg px-2.5 py-[5px] text-body-sm font-medium text-interactive transition-colors hover:bg-bark/[0.04]"
      >
        View all appointments
        <ArrowRight aria-hidden="true" className="size-3.5" />
      </Link>
    </div>
  );
}

type AvailableAppointmentList = Extract<
  AppointmentListResponse,
  { status: "available" }
>;

// Away from home there is no list data in scope, so the expanded panel
// fetches the first Appointments page itself (default statuses only) and
// shows what is still ahead today.
function SiderailTodayPanel({
  activeAppointmentId,
  listSearch,
}: {
  activeAppointmentId: string | null;
  listSearch: AppointmentListSearch;
}): ReactElement {
  const list = useSiderailTodayList(listSearch.rdId);
  const data = list.data?.data;
  // Dark, not "unavailable": nothing is broken and nothing was fetched.
  if (data?.status === "no_selection") return <></>;

  if (!data || data.status === "unavailable") {
    return <SiderailTodayStatus loading={!list.data && !list.isError} />;
  }

  return (
    <SiderailTodaySchedule
      data={data}
      activeAppointmentId={activeAppointmentId}
      listSearch={listSearch}
    />
  );
}

/**
 * The first Appointments page (default statuses only) for Today. The classic
 * panel mounts it only while visible; the rail's entry passes `enabled` so the
 * query runs only while Today shows.
 */
export function useSiderailTodayList(
  rdId: string | undefined,
  enabled = true
): UseQueryResult<{ data: AppointmentListResponse }> {
  const { getToken } = useAuth();
  const requestedTimezone = deviceTimezone();

  return useQuery({
    // Renders patient names, so the selected RD belongs in the key or a switch
    // leaves the previous RD's schedule on screen.
    queryKey: ["appointments-siderail", requestedTimezone, rdId ?? null],
    queryFn: () => {
      const request = appointmentsListApiRequest({
        showNonDefaultStatuses: false,
        cursor: null,
        timezone: requestedTimezone,
        ...(rdId ? { rdId } : {}),
      });

      return authenticatedApi<{ data: AppointmentListResponse }>(
        getToken,
        request.path,
        request.init
      );
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

function SiderailTodayStatus({ loading }: { loading: boolean }): ReactElement {
  return (
    <p className="px-2.5 pb-1 pt-2.5 text-xs text-subtle">
      {loading ? "Loading today…" : "Today’s schedule is unavailable."}
    </p>
  );
}

function SiderailTodaySchedule({
  data,
  activeAppointmentId,
  listSearch,
}: {
  data: AvailableAppointmentList;
  activeAppointmentId: string | null;
  listSearch: AppointmentListSearch;
}): ReactElement {
  const display = { now: data.now, timezone: data.timezone };
  const groups = groupAppointments(data.appointments, display);
  const todayCount = appointmentsTodayCount(groups, {
    ...display,
    hasNextPage: data.pageInfo.nextCursor !== null,
  });
  const todayRemaining =
    groups.find((group) => group.label === "Today")?.appointments ?? [];
  const upNextId = upNextAppointment(groups, display)?.appointment.id ?? null;

  return (
    <>
      <SiderailSectionLabel
        label={`Today · ${shortDateLabel(display.now, display.timezone)}`}
        count={todayCountLabel(todayRemaining.length, todayCount)}
      />
      {todayRemaining.length > 0 ? (
        todayRemaining.map((item) => (
          <SiderailTodayRow
            key={item.id}
            item={item}
            listSearch={listSearch}
            active={item.id === activeAppointmentId}
            upNext={item.id === upNextId}
            timezone={display.timezone}
          />
        ))
      ) : (
        <SiderailAllClear />
      )}
    </>
  );
}

function SiderailTodayRow({
  item,
  listSearch,
  active,
  upNext,
  timezone,
}: {
  item: AppointmentListItem;
  listSearch: AppointmentListSearch;
  active: boolean;
  upNext: boolean;
  timezone: string;
}): ReactElement {
  return (
    <Link
      to="/appointments/$appointmentId"
      params={{ appointmentId: item.id }}
      search={listSearch}
      aria-current={active ? "page" : undefined}
      className={siderailTodayRowClass(upNext, active)}
    >
      <SiderailTodayRowContent
        time={formatAppointmentTime(item.startsAt, timezone)}
        name={item.patientName || "Patient"}
        upNext={upNext}
      />
    </Link>
  );
}

function SiderailAllClear(): ReactElement {
  return (
    <div className="mx-1 mt-0.5 rounded-lg border border-sand-200/80 bg-card px-3.5 py-4">
      <p className="font-sans text-body font-bold text-muted-strong">
        All clear for today
      </p>
      <p className="mt-1 text-xs leading-[18px] text-subtle">
        Nothing else on your schedule — enjoy the breathing room.
      </p>
    </div>
  );
}
